import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import clinicService from '../../../services/clinic.service';
import { useEyeconxStore } from '../../../store/eyeconxStore';
import SubHeader from '../../layout/components/SubHeader';
import ReputationNav from './subparts/ReputationNav';
import ReputationSkeleton from './subparts/ReputationSkeleton';
import OverviewTab from './tabcards/OverviewTab';
import ReputationLinkTab from './tabcards/ReputationLinkTab';
import CommentsTab from './tabcards/CommentsTab';
import FeedbackTab from './tabcards/FeedbackTab';
import { EMPTY_PAGE, REPUTATION_TABS } from './subparts/reputationQuery';
import { REPUTATION_PAGE_THEME } from './subparts/reputationTheme';
import './Reputation.css';

function Reputation() {
    const clinicId = useEyeconxStore((state) => state.user?.clinicId);

    const [loading, setLoading] = useState(true);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [activeTab, setActiveTab] = useState('overview');
    const [loadedTabs, setLoadedTabs] = useState(() => new Set(['overview']));

    const [shortCode, setShortCode] = useState(null);
    const [reputationLinks, setReputationLinks] = useState([]);
    const [patientComments, setPatientComments] = useState(EMPTY_PAGE);
    const [patientFeedbacks, setPatientFeedbacks] = useState(EMPTY_PAGE);

    const fetchReputationData = useCallback(async (options = {}) => {
        const { silent = false } = options;

        if (!clinicId) {
            return;
        }

        if (!silent) {
            setLoading(true);
        }

        try {
            const [shortCodeRes, linksRes, commentsRes, feedbacksRes] = await Promise.all([
                clinicService.getReputationShortCode(clinicId),
                clinicService.getReputationLink(clinicId),
                clinicService.PatientComments(clinicId),
                clinicService.PatientFeedbacks(clinicId),
            ]);

            setShortCode(shortCodeRes.data);
            setReputationLinks(linksRes.data || []);
            setPatientComments(commentsRes.data || EMPTY_PAGE);
            setPatientFeedbacks(feedbacksRes.data || EMPTY_PAGE);
        } catch (error) {
            console.error('Error fetching reputation data:', error);
            toast.error('Failed to load reputation data');
        } finally {
            if (!silent) {
                setLoading(false);
            }
        }
    }, [clinicId]);

    useEffect(() => {
        fetchReputationData();
    }, [fetchReputationData]);

    const fetchPatientComments = async (page = 0, size = 10) => {
        try {
            const response = await clinicService.PatientComments(clinicId, page, size);
            setPatientComments(response.data || EMPTY_PAGE);
        } catch (error) {
            console.error('Error fetching patient comments:', error);
            toast.error('Failed to load patient comments');
        }
    };

    const fetchPatientFeedbacks = async (page = 0, size = 10) => {
        try {
            const response = await clinicService.PatientFeedbacks(clinicId, page, size);
            setPatientFeedbacks(response.data || EMPTY_PAGE);
        } catch (error) {
            console.error('Error fetching patient feedbacks:', error);
            toast.error('Failed to load patient feedback');
        }
    };

    const handleRefresh = async () => {
        setIsRefreshing(true);
        try {
            await fetchReputationData({ silent: true });
        } finally {
            setIsRefreshing(false);
        }
    };

    const handleTabChange = (tabId) => {
        setActiveTab(tabId);
        setLoadedTabs((prev) => new Set([...prev, tabId]));
    };

    const tabCounts = useMemo(
        () => ({
            'reputation-links': reputationLinks.length,
            comments: patientComments.totalElements ?? 0,
            feedbacks: patientFeedbacks.totalElements ?? 0,
        }),
        [reputationLinks.length, patientComments.totalElements, patientFeedbacks.totalElements]
    );

    const description = useMemo(() => {
        const commentCount = patientComments.totalElements ?? 0;
        const feedbackCount = patientFeedbacks.totalElements ?? 0;

        if (commentCount || feedbackCount) {
            return `Manage your clinic's online presence · ${commentCount} comment${commentCount === 1 ? '' : 's'} · ${feedbackCount} feedback response${feedbackCount === 1 ? '' : 's'}`;
        }

        return "Manage and monitor your clinic's online presence and patient feedback";
    }, [patientComments.totalElements, patientFeedbacks.totalElements]);

    const tabContent = useMemo(
        () => ({
            overview: (
                <OverviewTab
                    shortCode={shortCode}
                    reputationLinks={reputationLinks}
                    patientComments={patientComments}
                    patientFeedbacks={patientFeedbacks}
                />
            ),
            'reputation-links': <ReputationLinkTab reputationLinks={reputationLinks} />,
            comments: (
                <CommentsTab
                    patientComments={patientComments}
                    onPageChange={fetchPatientComments}
                />
            ),
            feedbacks: (
                <FeedbackTab
                    patientFeedbacks={patientFeedbacks}
                    onPageChange={fetchPatientFeedbacks}
                />
            ),
        }),
        [shortCode, reputationLinks, patientComments, patientFeedbacks]
    );

    return (
        <div className="ecx-page reputation-page ecx-page--full-width">
            <SubHeader
                title="Reputation"
                description={description}
                icon="bi-star"
                iconTheme={REPUTATION_PAGE_THEME}
                containerClass="w-100 px-4"
            >
                <button
                    type="button"
                    className="btn btn-light-primary btn-sm rounded-pill"
                    onClick={handleRefresh}
                    disabled={loading || isRefreshing}
                    title="Refresh reputation data"
                >
                    <i className={`bi bi-arrow-clockwise me-1 ${isRefreshing ? 'ecx-refresh-spin' : ''}`}></i>
                    Refresh
                </button>
            </SubHeader>

            <div className="ecx-page-body">
                <div className="ecx-page-card ecx-page-panel reputation-panel">
                    <div className="ecx-page-panel__header reputation-panel__header border-top-0">
                        <ReputationNav
                            tabs={REPUTATION_TABS}
                            activeTab={activeTab}
                            onTabChange={handleTabChange}
                            counts={tabCounts}
                        />
                    </div>

                    <div className="ecx-page-panel__body reputation-panel__body">
                        {loading ? (
                            <ReputationSkeleton />
                        ) : (
                            REPUTATION_TABS.map((tab) =>
                                loadedTabs.has(tab.id) ? (
                                    <div
                                        key={tab.id}
                                        className={
                                            activeTab === tab.id
                                                ? 'h-100 d-flex flex-column min-h-0'
                                                : 'd-none'
                                        }
                                    >
                                        {tabContent[tab.id]}
                                    </div>
                                ) : null
                            )
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default Reputation;
